/*
DashboardSidebar Attribution: https://github.com/mui/material-ui/tree/v7.3.4/docs/data/material/getting-started/templates/crud-dashboard
*/
import * as React from "react";
import { alpha, styled, Theme } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Stack from "@mui/material/Stack";
import { Link, useNavigate } from "react-router";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { Button, Menu, MenuItem } from "@mui/material";
import Link2 from "@mui/material/Link";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelectedAccount } from "@/contexts/SelectedAccountContext";
import LinkBehavior from "../LinkBehavior";
import LaunchIcon from "@mui/icons-material/Launch";
import CompanySelector from "@/components/DashboardSidebar/CompanySelector";
import { useCurrentUser } from "@/hooks/useProfileData";
import { hasEstablishedDynamicsContact } from "@/utils/dynamicsContact";

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  border: "none",
  boxShadow: "0 4px 16px 0 rgba(0,0,0,0.18), 0 2px 8px 0 rgba(0,0,0,0.14)",
  zIndex: theme.zIndex.drawer + 1,
}));

const profileMenuLinkStyles = {
  display: "block",
  padding: "6px 16px",
  width: "100%",
};

const profileMenuItemStyles = (theme: Theme) => ({
  padding: 0,
  "& .MuiLink-root": {
    color: "inherit",
  },
  "&:hover": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.hoverOpacity,
    ),
    "& .MuiLink-root": {
      color: theme.palette.error.main,
    },
  },
  "&:focus-within": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.focusOpacity,
    ),
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "-2px",
    "& .MuiLink-root": {
      color: theme.palette.error.main,
    },
  },
  "&:active": {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
    "& .MuiLink-root": {
      color: theme.palette.error.main,
    },
  },
});

const LogoWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  height: 56,
  minHeight: 56,
  width: 58,
  overflow: "visible",
  [theme.breakpoints.up("md")]: {
    marginLeft: "auto",
    width: 144,
  },
  [theme.breakpoints.up("lg")]: {
    marginRight: "2.25rem",
    marginLeft: "auto",
    width: 175,
  },
}));

const LogoImage = styled("img")(({ theme }) => ({
  position: "absolute",
  top: -12,
  left: 0,
  width: "100%",
  height: "auto",
  "&.coc-logo-mobile": {
    position: "relative",
    top: 0,
    left: "auto",
    width: "auto",
    maxHeight: 56,
  },
  [theme.breakpoints.up("md")]: {
    top: -14,
    "&.coc-logo-mobile": {
      maxHeight: 40,
      top: 0,
    },
  },
  [theme.breakpoints.up("lg")]: {
    top: -16,
  },
}));

interface DashboardHeaderProps {
  menuOpen: boolean;
  onToggleMenu: (open: boolean) => void;
  isAuthenticated?: boolean;
  accountDisplayName?: string;
  envName?: string;
  appName?: string;
  shortAppName?: string;
  mobileLogoSrc?: string;
  desktopLogoSrc?: string;
  authControl?: React.ReactNode;
  fixedHeader?: boolean;
}

function DashboardHeader({
  menuOpen,
  onToggleMenu,
  isAuthenticated,
  envName,
  appName,
  shortAppName,
  mobileLogoSrc,
  desktopLogoSrc,
  fixedHeader = false,
}: DashboardHeaderProps) {
  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const userMenuOpen = Boolean(anchorEl);

  const {
    companies,
    selectedAccountId,
    selectedOrganization: selectedCompany,
    setSelectedOrganizationId,
  } = useSelectedAccount();

  const { data: dynamicsContact, isLoading: isLoadingProfileContact } =
    useCurrentUser();
  const showProfileMenuItem =
    !isLoadingProfileContact &&
    hasEstablishedDynamicsContact(dynamicsContact);

  const handleProfileMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const { instance: _instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();

  const handleLogout = React.useCallback(() => {
    navigate("/signout");
  }, [navigate]);

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = "Expand";
      const collapseMenuActionText = "Collapse";

      return (
        <Tooltip
          title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
          enterDelay={1000}
        >
          <div>
            <IconButton
              size="small"
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText
                } navigation menu`}
              onClick={handleMenuOpen}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [handleMenuOpen],
  );

  const showEnvBadge = envName && envName.toLowerCase() !== "production";

  const appBar = (
    <AppBar
      color="inherit"
      position={fixedHeader ? "fixed" : "relative"}
      sx={{
        displayPrint: "none",
        ...(fixedHeader
          ? {}
          : {
            width: "100%",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }),
      }}
    >
      <Toolbar
        sx={{
          backgroundColor: "inherit",
          mx: { xs: -0.75, sm: -1 },
          py: 0,
          minHeight: 56,
          height: 56,
          alignItems: "stretch",
          "@media (min-width:600px)": {
            minHeight: 56,
            height: 56,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {isAuthenticated ? (
              <Box sx={{ mr: 1 }}>{getMenuIcon(menuOpen)}</Box>
            ) : null}
            <Link
              to="/"
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <LogoWrapper>
                <LogoImage
                  src={mobileLogoSrc}
                  alt="Return to Vehicle for Hire homepage"
                  className="coc-logo coc-logo-mobile no-print"
                  loading="lazy"
                />
                <LogoImage
                  src={desktopLogoSrc}
                  alt="Return to Vehicle for Hire homepage"
                  className="coc-logo coc-logo-desktop"
                  loading="lazy"
                />
              </LogoWrapper>
            </Link>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                pl: { xs: 0, sm: "10px" },
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: { xs: "none", sm: "block" },
                      color: "inherit",
                    }}
                    component="h1"
                    variant="h6"
                    noWrap
                  >
                    {appName}
                  </Typography>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxWidth: "45vw",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: { xs: "block", sm: "none" },
                      color: "inherit",
                    }}
                    component="h1"
                    variant="h6"
                    noWrap
                  >
                    {shortAppName}
                  </Typography>
                </Box>
                {showEnvBadge ? (
                  <Badge
                    color="primary"
                    badgeContent={envName}
                    sx={{
                      ml: 1,
                      pointerEvents: "none",
                      "& .MuiBadge-badge": {
                        transform: "none",
                        position: "relative",
                      },
                      display: "inline-flex",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: "block", width: 0, height: 0 }}
                    />
                  </Badge>
                ) : null}
              </Box>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              marginLeft: "auto",
              flexShrink: 0,
              flexWrap: "nowrap",
            }}
          >
            {isAuthenticated && (
              <>
                <>
                  <CompanySelector
                    companies={companies}
                    selectedCompany={selectedCompany}
                    selectedAccountId={selectedAccountId}
                    setSelectedOrganizationId={setSelectedOrganizationId}
                  />

                  <Button
                    id="profile-menu-button"
                    aria-controls={userMenuOpen ? "profile-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={userMenuOpen ? "true" : undefined}
                    onClick={handleProfileMenuClick}
                    sx={{
                      textTransform: "none",
                      color: (theme: Theme) =>
                        theme.palette.mode === "dark" ? "#FFA4A2" : "#c8102e",
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      minWidth: "auto",
                      padding: "8px 8px 8px 8px",
                      marginRight: "24px",
                      border: "3px solid transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "error.main",
                      },
                    }}
                  >
                    {account?.idTokenClaims?.["signInName"] as string}
                    <ArrowDropDownIcon sx={{ verticalAlign: "middle" }} />
                  </Button>
                </>
                <Menu
                  id="profile-menu"
                  anchorEl={anchorEl}
                  open={userMenuOpen}
                  onClose={handleProfileMenuClose}
                  slotProps={{
                    list: {
                      "aria-labelledby": "profile-menu-button",
                    },
                  }}
                >
                  {showProfileMenuItem ? (
                    <MenuItem
                      onClick={handleProfileMenuClose}
                      disableRipple={true}
                      sx={profileMenuItemStyles}
                    >
                      <Link2
                        component={LinkBehavior}
                        href="/profile"
                        underline="none"
                        color="inherit"
                        sx={profileMenuLinkStyles}
                      >
                        My Profile
                      </Link2>
                    </MenuItem>
                  ) : null}

                  <MenuItem
                    onClick={handleProfileMenuClose}
                    disableRipple={true}
                    sx={profileMenuItemStyles}
                  >
                    <Link2
                      href={`https://${process.env.REACT_APP_IDM}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      color="inherit"
                      sx={{
                        ...profileMenuLinkStyles,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      aria-label="myID (opens in new tab)"
                    >
                      myID
                      <LaunchIcon
                        sx={{ fontSize: "1rem", ml: 0.5 }}
                        aria-hidden="true"
                      />
                    </Link2>
                  </MenuItem>

                  {showProfileMenuItem ? (
                    <MenuItem
                      onClick={handleProfileMenuClose}
                      disableRipple={true}
                      sx={profileMenuItemStyles}
                    >
                      <Link2
                        component={LinkBehavior}
                        href="/paymenthistory"
                        underline="none"
                        color="inherit"
                        sx={profileMenuLinkStyles}
                      >
                        My Payment History
                      </Link2>
                    </MenuItem>
                  ) : null}

                  <MenuItem
                    onClick={() => {
                      handleProfileMenuClose();
                      handleLogout();
                    }}
                    sx={{
                      display: { xs: "flex" },
                    }}
                  >
                    Sign Out
                  </MenuItem>

                  <MenuItem>
                    <ThemeSwitcher />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );

  return appBar;
}

export default DashboardHeader;
